import { publicUrl } from '../routing/domains';
import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { useAuth } from '../auth/useAuth';
import { useColorblindMode } from '../hooks/useColorblindMode';
import { BrandLogo } from './BrandLogo';

export default function Header({ onToggleSidebar }: { onToggleSidebar: () => void }) {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const { enabled: colorblindEnabled, toggle: toggleColorblind } = useColorblindMode();

  // Fetch the user's display name from Cognito attributes
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    async function loadName() {
      try {
        const attrs = await fetchUserAttributes();
        if (cancelled) return;
        const name =
          attrs?.['custom:display_name'] ||
          attrs?.name ||
          attrs?.given_name ||
          user?.username ||
          'Trader';
        setDisplayName(name);
      } catch {
        if (!cancelled) setDisplayName(user?.username ?? 'Trader');
      }
    }

    loadName();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between border-b border-slate-800/80 bg-[#070b12]/90 backdrop-blur-md px-4 text-white shadow-sm">
      {/* Left: hamburger + brand */}
      <div className="flex items-center gap-3">
        <button
          onClick={onToggleSidebar}
          className="glass-button rounded-lg p-1.5 hover:bg-slate-800/70 hover:border-cyan-400/30 lg:hidden"
          aria-label="Toggle sidebar"
        >
          <svg className="h-5 w-5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <a
          href={publicUrl('/')}
          onClick={(e) => {
            if (window.location.pathname === '/' || window.location.pathname === '') {
              e.preventDefault();
              try {
                window.scrollTo?.({ top: 0, left: 0, behavior: 'instant' });
                window.location.href = publicUrl('/');
                if (typeof window.location.reload === 'function') {
                  window.location.reload();
                }
              } catch {
                window.scrollTo?.({ top: 0, left: 0, behavior: 'instant' });
              }
            }
          }}
          className="outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded group"
        >
          <BrandLogo size="sm" />
        </a>
      </div>

      {/* Right: user menu */}
      <div ref={menuRef} className="relative">
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="glass-button flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm hover:border-cyan-400/40"
        >
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-cyan-500/20 text-cyan-300 text-xs font-semibold border border-cyan-400/40 shadow-[0_0_10px_rgba(56,189,248,0.2)]">
            {(displayName || 'T').charAt(0).toUpperCase()}
          </div>
          <span className="hidden sm:inline font-medium text-slate-200">{displayName || 'Trader'}</span>
          <svg className={`h-4 w-4 text-slate-400 transition-transform ${menuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {menuOpen && (
          <div className="glass-panel absolute right-0 z-50 mt-2 w-52 rounded-xl border border-slate-700/80 bg-[#0a0f18]/95 py-1.5 shadow-2xl shadow-black/80 backdrop-blur-xl">
            <Link
              to="/profile"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800/60 hover:text-cyan-300 transition-colors"
            >
              <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0" />
              </svg>
              Profile &amp; Security
            </Link>
            <Link
              to="/brokers"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-300 hover:bg-white/[0.08] hover:text-white transition-colors"
            >
              <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
              </svg>
              Broker Connections
            </Link>
            <Link
              to="/config"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-300 hover:bg-white/[0.08] hover:text-white transition-colors"
            >
              <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0m-9.75 0h9.75" />
              </svg>
              Settings
            </Link>
            <button
              onClick={toggleColorblind}
              className="flex w-full items-center justify-between px-4 py-2 text-sm text-gray-300 hover:bg-white/[0.08] hover:text-white transition-colors"
            >
              <span className="flex items-center gap-2.5">
                <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Colorblind Mode
              </span>
              <span
                className={`inline-block h-4 w-7 rounded-full transition-colors ${
                  colorblindEnabled ? 'bg-blue-500' : 'bg-white/20'
                }`}
              >
                <span
                  className={`block h-3 w-3 translate-y-0.5 rounded-full bg-white transition-transform ${
                    colorblindEnabled ? 'translate-x-3.5' : 'translate-x-0.5'
                  }`}
                />
              </span>
            </button>
            <hr className="my-1.5 border-white/10" />
            <button
              onClick={() => { setMenuOpen(false); logout(); }}
              className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
            >
              <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
              </svg>
              Sign Out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
